import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";
import Utils from "ui/helpers/Utils";
import {CanvasBlendingMode} from "ui/canvas/CanvasBlendingMode";

/**
 * @typedef {Object} T_LayerPropsOptions
 * @property {CanvasLayer} layer
 */

/**
 * Layer Properties
 */
export default class LayerProps extends Dialog {
	/**
	 * Class Constructor
	 *
	 * @param {T_LayerPropsOptions} [options]
	 */
	constructor(options = {}) {
		super({
			title: i18n('title.layer-props'),
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		/**
		 * @type {T_LayerPropsOptions}
		 * @private
		 */
		this._options = options;

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(data:boolean)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * Returns promise which will be resolved in any case with one of two values:
	 * 1. Object - if form is ok and submitted;
	 * 2. null - if form has been closed;
	 *
	 * @returns {Promise<Object|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * Handles click by button
	 *
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * Applies form data and destroy Dialog Box if all ok
	 *
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._options.layer) {
			this._options.layer.updateProps(data);
		}
		if (this._onComplete) {
			this._onComplete(true);
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * Builds scene properties form
	 *
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: this._options.layer ? this._options.layer.getProps() : null,
			fields: [
				{
					type: 'text',
					name: 'title',
					caption: i18n('inputs.layer.title'),
					fullWidth: true,
					required: true,
					maxlength: 64,
					validators: [
						(value) => {
							if (value.length < 1 || value.length > 32) {
								return i18n('errors.layer.title-length', 3, 32);
							}

							return '';
						}
					]
				},
				{
					type: 'checkbox',
					name: 'isVisible',
					caption: i18n('inputs.layer.visible'),
				},
				{
					type: 'range',
					name: 'opacity',
					min: 10,
					max: 100,
					step: 10,
					caption: i18n('inputs.layer.opacity'),
				},
				{
					type: 'radio',
					name: 'blend',
					options: [
						{text: i18n('inputs.layer.blend-modes.normal'), value: CanvasBlendingMode.NORMAL},
						{text: i18n('inputs.layer.blend-modes.overlay'), value: CanvasBlendingMode.OVERLAY},
						{text: i18n('inputs.layer.blend-modes.multiply'), value: CanvasBlendingMode.MULTIPLY},
						{text: i18n('inputs.layer.blend-modes.screen'), value: CanvasBlendingMode.SCREEN},
						{text: i18n('inputs.layer.blend-modes.hard-light'), value: CanvasBlendingMode.HARD_LIGHT},
						{text: i18n('inputs.layer.blend-modes.darken'), value: CanvasBlendingMode.DARKEN},
						{text: i18n('inputs.layer.blend-modes.lighten'), value: CanvasBlendingMode.LIGHTEN},
					],
					caption: i18n('inputs.layer.blend'),
				}
			]
		});
		this.setContent(el);
		// focus first input
		await Utils.wait(40);
		let inp = el.querySelector('input');
		inp.focus();
		inp.select();
	}

	/**
	 * Destroys the dialog box
	 *
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(false);
			this._onComplete = null;
		}
		super.destroy();
	}
}
